import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileCheck2 } from "lucide-react";
import api from "../lib/api";
import { fmtDate, fmtKc } from "../lib/format";
import { Modal } from "./Modal";

const inputCls = "w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm";
const todayISO = () => new Date().toISOString().slice(0, 10);

export function TaxDocumentModal({ invoice, onClose, onCreated }) {
    const [date, setDate] = useState((invoice.paid_at || "").slice(0, 10) || todayISO());
    const [amount, setAmount] = useState(invoice.tax_doc_remaining ?? invoice.total);
    const [busy, setBusy] = useState(false);
    const rate = Number(invoice.vat_rate) || 0;
    const base = Number(amount) / (1 + rate / 100);
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/invoices/${invoice.id}/tax-document`, { payment_date: date, amount: Number(amount) });
            toast.success(`Daňový doklad ${data.number} vystaven`);
            onCreated(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Vystavení selhalo"); }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Daňový doklad k přijaté platbě" subtitle={`Zálohová faktura ${invoice.number} · ${invoice.client_name}`} onClose={onClose} testid="tax-doc-modal"
               footer={<div className="flex gap-2 justify-end"><button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 rounded-sm">Později</button>
                   <button onClick={submit} disabled={busy} data-testid="tax-doc-submit" className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm disabled:opacity-50">Vystavit (řada DD)</button></div>}>
            <div className="space-y-4">
                <p className="text-sm text-slate-600">Platba za zálohu byla přijata. Plátce DPH musí do 15 dnů vystavit daňový doklad k přijaté platbě – DUZP je den přijetí platby.</p>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="label-eyebrow block mb-1.5">Datum přijetí platby (DUZP)</label><input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="tax-doc-date" className={inputCls} /></div>
                    <div><label className="label-eyebrow block mb-1.5">Přijatá částka (s DPH)</label><input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="tax-doc-amount" className={inputCls} /></div>
                </div>
                <div className="text-xs text-slate-500" data-testid="tax-doc-preview">Základ {fmtKc(base)} + DPH {rate} % {fmtKc(Number(amount) - base)} · zbývá nezdaněno {fmtKc(invoice.tax_doc_remaining)}</div>
            </div>
        </Modal>
    );
}

export function TaxDocumentsCard({ invoice, onOpen }) {
    const docs = invoice.tax_documents || [];
    if (invoice.kind !== "zalohova" || invoice.imported) return null;
    return (
        <div className={`bg-white border mb-6 ${invoice.needs_tax_doc ? "border-amber-400" : "border-slate-200"}`} data-testid="tax-documents-card">
            <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Daňové doklady k přijaté platbě</div>
                    <div className="text-xs text-slate-500 mt-1">
                        {!invoice.tax_doc_eligible ? (invoice.vat_mode === "pdp" ? "Režim přenesené daňové povinnosti – daňový doklad k platbě se nevystavuje (daň odvede odběratel)." : "Neplátce DPH – daňový doklad k platbě se nevystavuje.")
                            : invoice.needs_tax_doc ? <span className="text-amber-800 font-semibold" data-testid="tax-doc-needed">Nezdaněno {fmtKc(invoice.tax_doc_remaining)} · vystavit do {fmtDate(invoice.tax_doc_deadline)}</span>
                            : docs.length ? "Záloha je zdaněna v plné výši." : "Po označení zálohy jako uhrazené nabídneme vystavení dokladu."}
                    </div>
                </div>
                {invoice.tax_doc_eligible && invoice.tax_doc_remaining > 0.005 && invoice.status !== "stornovana" && (
                    <button onClick={onOpen} data-testid="tax-doc-open" className="px-3 py-2 text-xs uppercase tracking-wider font-semibold bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm inline-flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5" /> Vystavit daňový doklad k platbě</button>
                )}
            </div>
            {docs.length > 0 && (
                <div className="divide-y divide-slate-100">
                    {docs.map((d) => (
                        <div key={d.invoice_id} className="flex items-center justify-between px-6 py-3 text-sm gap-3 flex-wrap" data-testid={`tax-doc-row-${d.invoice_id}`}>
                            <Link to={`/faktury/${d.invoice_id}`} className="font-mono text-emerald-800 hover:underline">{d.number}</Link>
                            <span className="text-slate-500 text-xs">platba {fmtDate(d.payment_date)}</span>
                            <span className="tabular-data font-semibold">{fmtKc(d.total)}</span>
                        </div>
                    ))}
                    <div className="flex items-center justify-between px-6 py-2 text-xs text-slate-600"><span>Zdaněno celkem</span><span className="tabular-data">{fmtKc(invoice.tax_doc_total)} z {fmtKc(invoice.total)}</span></div>
                </div>
            )}
        </div>
    );
}
